/* ============================================================
   FEGO — shared behaviour for custom sections.
   Relies on window.Swiper (already bundled by the Jhango theme).
   All custom elements are namespaced fego-* and guard against
   double-definition so they are safe to load once per section.
   ============================================================ */
(function () {
  'use strict'

  /* -------------------------------------------------------
     <fego-slider> — full-bleed Swiper with configurable
     peek/partial slides on BOTH mobile and desktop.
     ------------------------------------------------------- */
  if (!customElements.get('fego-slider')) {
    class FegoSlider extends HTMLElement {
      constructor () {
        super()
        this.slider = null
        document.addEventListener('shopify:section:load', (e) => {
          if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.build()
        })
      }
      connectedCallback () {
        if (window.Swiper) { this.build() }
        else { window.addEventListener('load', () => this.build(), { once: true }) }
      }
      num (v, d) { const n = Number(v); return isNaN(n) ? d : n }
      perView (base, partial, peek) {
        let p = this.num(peek, 0.2)
        if (p > 1) p = p / 100 // accept either a fraction (0.2) or a percentage (20)
        return partial ? base + p : base
      }
      build () {
        if (!window.Swiper) return
        if (this.slider) { this.slider.destroy(true, true); this.slider = null }
        const d = this.dataset
        const pMob = d.partialMobile === 'true'
        const pDesk = d.partialDesktop === 'true'
        const space = this.num(d.space, 18)
        this.slider = new window.Swiper(this.querySelector('.swiper'), {
          speed: this.num(d.speed, 400),
          rewind: true,
          watchSlidesProgress: true,
          autoplay: d.autoplay === '0' || !d.autoplay ? false : { delay: this.num(d.autoplay, 0) * 1000 },
          navigation: { enabled: d.nav === 'true', prevEl: this.querySelector('.swiper-button-prev'), nextEl: this.querySelector('.swiper-button-next') },
          pagination: { enabled: d.pagination === 'true', el: this.querySelector('.swiper-pagination'), type: d.paginationType || 'bullets', dynamicBullets: true },
          scrollbar: { enabled: d.scrollbar === 'true', el: this.querySelector('.swiper-scrollbar'), draggable: true },
          mousewheel: { enabled: true, forceToAxis: true },
          breakpoints: {
            0:    { slidesPerView: this.perView(this.num(d.xs, 1), pMob, d.peekMobile), spaceBetween: space },
            500:  { slidesPerView: this.perView(this.num(d.sm, 2), pMob, d.peekMobile), spaceBetween: space },
            800:  { slidesPerView: this.perView(this.num(d.md, 3), pDesk, d.peekDesktop), spaceBetween: space },
            1200: { slidesPerView: this.perView(this.num(d.lg, 4), pDesk, d.peekDesktop), spaceBetween: space },
            1600: { slidesPerView: this.perView(this.num(d.xl, 4), pDesk, d.peekDesktop), spaceBetween: space }
          }
        })
      }
    }
    customElements.define('fego-slider', FegoSlider)
  }

  /* -------------------------------------------------------
     <fego-bike-finder> — Brand / Model / Year selector.
     data-map = JSON { "Brand": { "Model": ["2024","2023"] } }
     data-action = base url; submits to /search?q= or a link.
     ------------------------------------------------------- */
  if (!customElements.get('fego-bike-finder')) {
    class FegoBikeFinder extends HTMLElement {
      connectedCallback () {
        this.map = {}
        try { this.map = JSON.parse(this.dataset.map || '{}') } catch (e) { this.map = {} }
        this.brand = this.querySelector('[data-fego-brand]')
        this.model = this.querySelector('[data-fego-model]')
        this.year = this.querySelector('[data-fego-year]')
        this.btn = this.querySelector('[data-fego-find]')
        if (!this.brand) return
        this.fill(this.brand, Object.keys(this.map), 'Brand')
        this.brand.addEventListener('change', () => this.onBrand())
        if (this.model) this.model.addEventListener('change', () => this.check())
        if (this.year) this.year.addEventListener('change', () => this.check())
        if (this.btn) this.btn.addEventListener('click', () => this.go())
        this.check()
      }
      fill (sel, items, ph) {
        sel.innerHTML = '<option value="">' + ph + '</option>' + items.map(i => '<option value="' + i + '">' + i + '</option>').join('')
      }
      onBrand () {
        const b = this.brand.value
        const models = b && this.map[b] ? (Array.isArray(this.map[b]) ? this.map[b] : Object.keys(this.map[b])) : []
        if (this.model) this.fill(this.model, models, 'Model')
        if (this.year) {
          let years = []
          if (b && this.map[b] && !Array.isArray(this.map[b])) {
            // years depend on model — refreshed on model change
          }
          this.fill(this.year, years.length ? years : this.defaultYears(), 'Year')
        }
        this.check()
      }
      defaultYears () { const y = []; for (let i = 2025; i >= 2016; i--) y.push(String(i)); return y }
      check () {
        const ok = this.brand.value && (!this.model || this.model.value) && (!this.year || this.year.value)
        if (this.btn) { this.btn.classList.toggle('is-disabled', !ok); this.btn.disabled = !ok }
      }
      urlFor (b, m) {
        const entry = b && this.map[b]
        return (entry && !Array.isArray(entry)) ? (entry[m] || null) : null
      }
      go () {
        if (this.btn && this.btn.disabled) return
        // map may carry the product URL per model — go straight there instead of guessing via search
        const direct = this.urlFor(this.brand.value, this.model ? this.model.value : '')
        if (direct) { window.location.href = direct; return }
        const parts = [this.brand.value, this.model ? this.model.value : '', this.year ? this.year.value : ''].filter(Boolean)
        const base = this.dataset.action || '/search'
        const q = encodeURIComponent(parts.join(' '))
        window.location.href = base.indexOf('?') > -1 ? base + q : base + '?q=' + q + '&type=product'
      }
    }
    customElements.define('fego-bike-finder', FegoBikeFinder)
  }

  /* -------------------------------------------------------
     <fego-countup> — animates .fego-num[data-count] into view.
     ------------------------------------------------------- */
  if (!customElements.get('fego-countup')) {
    class FegoCountup extends HTMLElement {
      connectedCallback () {
        const nums = this.querySelectorAll('.fego-num[data-count]')
        if (!nums.length) return
        let done = false
        const io = new IntersectionObserver((entries) => {
          entries.forEach(en => {
            if (en.isIntersecting && !done) {
              done = true
              nums.forEach(el => {
                const target = Number(el.dataset.count) || 0
                const sfx = el.dataset.suffix || ''
                const step = Math.max(1, Math.round(target / 40))
                let c = 0
                const t = setInterval(() => { c += step; if (c >= target) { c = target; clearInterval(t) } el.textContent = c + sfx }, 22)
              })
            }
          })
        }, { threshold: 0.4 })
        io.observe(this)
      }
    }
    customElements.define('fego-countup', FegoCountup)
  }

  /* -------------------------------------------------------
     <fego-fit-title> — PDP signature: dynamic vehicle title.
     Grey "your vehicle" -> lime "BRAND MODEL" on selection.
     Wraps its own Brand/Model selects.
     ------------------------------------------------------- */
  if (!customElements.get('fego-fit-title')) {
    class FegoFitTitle extends HTMLElement {
      connectedCallback () {
        this.map = {}
        try { this.map = JSON.parse(this.dataset.map || '{}') } catch (e) { this.map = {} }
        this.brand = this.querySelector('[data-fego-brand]')
        this.model = this.querySelector('[data-fego-model]')
        this.vehEl = this.querySelector('[data-fego-veh]')
        this.fit = this.querySelector('[data-fego-fitline]')
        this.fitText = this.querySelector('[data-fego-fittext]')
        this.buttons = this.querySelectorAll('[data-fego-gate]')
        this.placeholder = this.dataset.placeholder || 'YOUR VEHICLE'
        if (!this.brand) return
        this.fill(this.brand, Object.keys(this.map), 'Select brand')
        if (this.model) this.model.disabled = true
        this.brand.addEventListener('change', () => this.onBrand())
        if (this.model) this.model.addEventListener('change', () => this.onModel())
        this.preselect()
      }
      urlFor (b, m) {
        const entry = b && this.map[b]
        return (entry && !Array.isArray(entry)) ? (entry[m] || null) : null
      }
      // The PDP knows its own vehicle from the product metafields: fill it in so the
      // title reads right and the buy buttons aren't gated behind a redundant question.
      preselect () {
        this.current = ''
        const b = this.dataset.selectedBrand || ''
        const m = this.dataset.selectedModel || ''
        if (!b) return
        this.add(this.brand, b)
        this.brand.value = b
        this.onBrand()
        if (this.model && m) { this.add(this.model, m); this.model.value = m }
        this.current = b + '|' + m
        this.onModel()
      }
      add (sel, val) {
        if (!Array.from(sel.options).some(o => o.value === val)) sel.add(new Option(val, val))
      }
      fill (sel, items, ph) {
        sel.innerHTML = '<option value="">' + ph + '</option>' + items.map(i => '<option value="' + i + '">' + i + '</option>').join('')
      }
      onBrand () {
        const b = this.brand.value
        this.brand.classList.toggle('is-filled', !!b)
        if (this.model) {
          this.model.disabled = !b
          this.model.classList.remove('is-filled')
          const models = b && this.map[b] ? (Array.isArray(this.map[b]) ? this.map[b] : Object.keys(this.map[b])) : []
          this.fill(this.model, models, 'Select model')
        }
        this.reset()
      }
      onModel () {
        if (!this.model.value) { this.reset(); return }
        const url = this.urlFor(this.brand.value, this.model.value)
        if (url && (this.brand.value + '|' + this.model.value) !== this.current) {
          window.location.href = url
          return
        }
        this.set()
      }
      set () {
        const label = (this.brand.value + ' ' + this.model.value).toUpperCase()
        if (this.vehEl) { this.vehEl.textContent = label; this.vehEl.classList.remove('is-empty'); this.vehEl.classList.add('is-set', 'is-flash'); setTimeout(() => this.vehEl.classList.remove('is-flash'), 700) }
        this.model.classList.add('is-filled')
        if (this.fit) this.fit.classList.add('is-ok')
        if (this.fitText) this.fitText.textContent = 'Fits your ' + this.brand.value + ' ' + this.model.value
        this.buttons.forEach(b => { b.disabled = false; b.classList.remove('is-disabled'); if (b.dataset.readyLabel) b.textContent = b.dataset.readyLabel })
        this.dispatch(true)
      }
      reset () {
        if (this.vehEl) { this.vehEl.textContent = this.placeholder; this.vehEl.classList.add('is-empty'); this.vehEl.classList.remove('is-set') }
        if (this.fit) this.fit.classList.remove('is-ok')
        if (this.fitText) this.fitText.textContent = this.dataset.idleText || 'Pick your bike to confirm fit & price'
        this.buttons.forEach(b => { b.disabled = true; b.classList.add('is-disabled'); if (b.dataset.idleLabel) b.textContent = b.dataset.idleLabel })
        this.dispatch(false)
      }
      dispatch (ready) {
        document.dispatchEvent(new CustomEvent('fego:fit', { detail: { ready: ready, brand: this.brand.value, model: this.model ? this.model.value : '' } }))
      }
    }
    customElements.define('fego-fit-title', FegoFitTitle)
  }

  /* -------------------------------------------------------
     <fego-sticky-bar> — reveals when its trigger scrolls off.
     data-trigger = selector to observe.
     ------------------------------------------------------- */
  if (!customElements.get('fego-sticky-bar')) {
    class FegoStickyBar extends HTMLElement {
      connectedCallback () {
        const target = document.querySelector(this.dataset.trigger || '.fego-buybox')
        if (!target) return
        new IntersectionObserver((es) => es.forEach(e => {
          this.classList.toggle('is-show', !e.isIntersecting && e.boundingClientRect.top < 0)
        }), { threshold: 0 }).observe(target)
      }
    }
    customElements.define('fego-sticky-bar', FegoStickyBar)
  }
})()
